/**
 * English strings (§14: `t('key')` over typed dictionaries). `{name}` placeholders are
 * filled by `t(key, { name })`. `bg.ts` overrides any subset of these keys.
 */
export const en = {
  'app.title': 'Mirror 360 CRM',

  'nav.new': 'New contact',
  'nav.followUps': 'Follow-ups',
  'nav.table': 'Table',
  'nav.log': 'Log',
  'nav.me': 'Me',
  'nav.admin': 'Admin',
  'nav.shortcuts': 'Shortcuts',
  'nav.signOut': 'Sign out',
  'viewAs.button': 'View as…',
  'viewAs.title': 'View as',
  'viewAs.banner': 'Viewing as {name}. Filters and the digest are theirs; anything you save is still yours.',
  'viewAs.back': 'Back to me',
  'viewAs.me': 'Me',
  'viewAs.unmapped': '(not mapped to a Notion person)',
  'digest.sendTo': 'Send to {name}',

  'shell.search': 'Search company, person, email, phone…',
  'shell.pending': '{n} pending',
  'shell.live': 'Live',
  'shell.connecting': 'Connecting',
  'shell.offline': 'Offline',

  'login.lead': 'Sign in with your Google account. Only invited addresses can enter.',
  'login.dev': 'Sign in (dev login)',
  'login.google': 'Sign in with Google',
  'login.mock': 'Mock mode: no Notion token configured.',
  'login.offline': 'You seem to be offline. Reads need one sign-in first.',

  'help.title': 'Keyboard shortcuts',
  'help.context': 'Context: {context}',
  'help.context.list': 'list (filters)',
  'help.context.record': 'record (sets fields)',
  'help.everywhere': 'Everywhere',
  'help.listTitle': 'In a list: filter',
  'help.recordTitle': 'In a record: set',
  'help.viewTitle': 'In this view',
  'help.cheatsheet': 'This cheat sheet',
  'help.ask': 'Ask the CRM a question',
  'help.views': 'New contact · Follow-ups · Table · Log',
  'help.new': 'New contact',
  'help.search': 'Search',
  'help.move': 'Move selection',
  'help.open': 'Open selected contact',
  'help.openNotion': 'Open selected contact in Notion',
  'help.esc': 'Close · clear search · blur',
  'help.undo': 'Revert my last action',
  'help.filterStage': 'Filter by Stage',
  'help.filterAction': 'Filter by Next Action',
  'help.filterStatus': 'Filter by Status',
  'help.filterOwner': 'Filter by Owner',
  'help.mine': 'Mine only',
  'help.due': 'Due today + overdue',
  'help.week': 'Due by end of this week',
  'help.month': 'Due by end of this month',
  'help.clear': 'Clear all filters',
  'help.rowActions': 'On the selected row: Followed up · Snooze · Note',
  'help.columns': 'Choose columns',
  'help.group': 'Grouping bar: show / hide',
  'help.collapse': 'Collapse / expand the selected row’s group',
  'help.setStage': 'Set Stage',
  'help.setAction': 'Set Next Action',
  'help.setStatus': 'Set Status',
  'help.setOwner': 'Set Owner',
  'help.assignMe': 'Assign to me',
  'help.date': 'Next contact date',
  'help.snooze7': 'Snooze +7 days',
  'help.followedUp': 'Followed up',
  'help.message': 'Message: AI draft, edit, mark sent',
  'help.snooze': 'Snooze',
  'help.note': 'Add note',
  'help.edit': 'Edit name · contact · email · phone',
  'help.save': 'Save the form',
  'help.acceptMatch': 'Accept the suggested known contact',
  'help.revertSelected': 'Revert the selected entry',

  'chip.stage': 'Stage',
  'chip.action': 'Action',
  'chip.status': 'Status',
  'chip.owner': 'Owner',
  'chip.mine': 'Mine',
  'chip.due': 'Due',
  'chip.week': 'This week',
  'chip.month': 'This month',
  'chip.archived': 'Archived',
  'chip.clear': 'Clear',
  'chip.clearOne': 'Clear {name} filter',

  'picker.toggle': 'toggle',
  'picker.done': 'done',
  'picker.pick': 'pick',
  'picker.filter': 'Type to filter…',
  'picker.noMatch': 'No match',
  'picker.clearValue': '— clear —',

  'date.inDays': 'In {n} days',
  'date.days': '{n} days',
  'date.custom': 'Custom…',
  'date.none': 'None',
  'date.now': 'now {date} ({n}d)',
  'date.set': 'Set',
  'date.noDate': 'no date',
  'date.noNextDate': 'no next contact date',

  'dialog.save': 'Save',
  'dialog.cancel': 'Cancel',
  'dialog.back': 'Back',
  'dialog.sent': 'sent',
  'dialog.hintSave': 'save',
  'dialog.hintCancel': 'cancel',

  'note.title': 'Note · {name}',
  'note.placeholder': 'What happened?',
  'note.add': 'Add note',

  'followedUp.title': 'Followed up · {name}',
  'followedUp.next': 'Next follow-up',
  'followedUp.nextAction': 'Next action',
  'followedUp.note': 'Note (optional)',
  'followedUp.notePlaceholder': 'How did it go?',
  'followedUp.button': '✓ Followed up',

  'edit.title': 'Edit',
  'edit.company': 'Company',
  'edit.contact': 'Contact',
  'edit.email': 'Email',
  'edit.phone': 'Phone',

  'revert.title': 'Revert · {type}',
  'revert.willApply': 'This will apply:',
  'revert.button': 'Revert',
  'revert.hint': 'revert',
  'revert.cannot': 'Cannot revert: {error}',

  'archive.title': 'Archive contact',
  'archive.body': 'Archive {name}? It moves to Notion’s trash (30 days) and can be restored from the log.',
  'archive.button': 'Archive',
  'archive.hint': 'archive',

  'message.title': 'Message · {name}',
  'message.cycle': '↻ Cycle',
  'message.nextDraft': 'Next draft',
  'message.more': '✨ Request more from Gemini',
  'message.asking': 'Asking Gemini…',
  'message.copy': 'Copy',
  'message.draftingFive': 'Drafting five versions…',
  'message.placeholderAi': 'Write or pick a draft',
  'message.placeholderManual': 'Write the message you sent',
  'message.geminiDrafting': 'Gemini is drafting…',
  'message.origin': 'Origin: {origin}',
  'message.toFrom': 'to {to} · from {from}',
  'message.steer.shorter': 'Shorter',
  'message.steer.warmer': 'Warmer',
  'message.steer.direct': 'More direct',
  'message.steer.call': 'Ask for a call',
  'message.steer.bg': 'In Bulgarian',
  'message.steerPlaceholder': 'Steer: e.g. mention the HR summit',
  'message.redraft': 'Redraft',
  'message.drafting': 'Drafting…',
  'message.sentNext': 'Sent. Next follow-up',
  'message.sent': '✓ Sent',

  'origin.ai': 'AI draft',
  'origin.ai-edited': 'AI draft, edited',
  'origin.manual': 'written by hand',

  'timeline.empty': 'No notes or changes yet. Press {key} to add a note.',
  'timeline.revert': 'Revert',
  'timeline.someone': 'someone',
  'timeline.notion': 'Notion',
  'timeline.system': 'system',
  'badge.pending': 'pending',
  'badge.reverted': 'reverted',
  'badge.conflict': 'conflict',
  'badge.conflictTitle': 'Someone else had changed this field; the earlier value is in the log',

  'logType.note': 'Note',
  'logType.create': 'Created',
  'logType.update': 'Updated',
  'logType.follow-up': 'Followed up',
  'logType.snooze': 'Snoozed',
  'logType.archive': 'Archived',
  'logType.restore': 'Restored',
  'logType.suggestion': 'Suggested message',
  'logType.external': 'Edited in Notion',
  'logType.revert': 'Reverted',
  'logType.message': 'Message sent',

  'changes.archived': 'Archived',
  'changes.restored': 'Restored',
  'changes.noteRemoved': 'Note removed',
  'changes.noteRestored': 'Note restored',

  'panel.openInNotion': 'Open in Notion',
  'panel.archived': 'archived',
  'panel.saving': 'Saving…',
  'panel.close': 'Close',
  'nav.ask': 'Ask',
  'ask.title': 'Ask the CRM',
  'ask.intro': 'Ask anything about the contacts and the log. Answers come from the CRM data only, nothing is changed. Try:',
  'ask.example1': 'Who is overdue in iGaming?',
  'ask.example2': 'Which contacts did we last touch more than 30 days ago?',
  'ask.example3': 'What did we agree with Acme Games?',
  'ask.placeholder': 'Your question…',
  'ask.hint': 'Enter sends · Shift+Enter for a new line',
  'ask.send': 'Ask',
  'ask.thinking': 'Thinking…',
  'ask.loading': 'Loading…',
  'ask.clear': 'Clear history',
  'ask.failed': 'The CRM could not answer. Try again.',
  'ask.off': 'Questions need AI, which is not configured on this server (GEMINI_API_KEY).',
  'panel.clickToChange': 'Click to change',
  'panel.followedUp': '✓ Followed up',
  'panel.message': '✉ Message',
  'panel.snooze': '😴 Snooze',
  'panel.note': '＋ Note',
  'panel.edit': 'Edit',
  'panel.archive': 'Archive',
  'panel.noCompany': '(no company)',
  'panel.suggested': '✨ Suggested message',
  'panel.otherFields': 'Other Notion fields',
  'panel.timeline': 'Timeline',
  'panel.created': 'Created {date}',
  'panel.edited': 'Edited {date}',
  'panel.by': 'by {name}',
  'panel.app': 'app',

  'field.stage': 'Stage',
  'field.status': 'Status',
  'field.nextAction': 'Next action',
  'field.owner': 'Owner',
  'field.nextContact': 'Next contact',
  'field.lastContact': 'Last contact',
  'field.email': 'Email',
  'field.phone': 'Phone',
  'field.linkedin': 'LinkedIn',
  'field.site': 'Site',
  'field.leadSource': 'Lead source',
  'field.industry': 'Industry',
  'field.description': 'Description',
  'field.role': 'Role',
  'field.created': 'Created',
  'field.edited': 'Edited',
  'field.company': 'Company',
  'field.contact': 'Contact',

  'due.overdue': '{n}d overdue',
  'due.today': 'today',
  'due.in': 'in {n}d',
  'due.late': '{n}d late',
  'due.short': '{n}d',

  'table.contacts': '{n} contacts',
  'table.of': '{n} of {total}',
  'table.columns': 'Columns',
  'table.opening': 'Opening local database…',
  'table.empty': 'No contacts yet. The first sync from Notion is on its way.',
  'table.noMatch': 'Nothing matches these filters.',

  'group.overdue': 'Overdue',
  'group.today': 'Today',
  'group.week': 'This week',
  'group.later': 'Later',
  'group.noDate': 'No date',
  'group.none': 'None',
  'group.due': 'Due',
  'group.by': 'Group by',
  'group.then': 'then',
  'group.add': '+ field',
  'group.remove': 'Remove this level',
  'group.clear': 'Ungroup',
  'group.level': 'Group level {n}',
  'group.toggle': 'Show or hide the grouping bar (g)',
  'followUps.count': '{n} to follow up',
  'followUps.sendDigest': 'Send digest now',
  'digest.title': 'Your digest',
  'digest.lead': 'What WhatsApp would show right now ({n} characters).',
  'digest.send': 'Send to my WhatsApp',
  'digest.setup': 'Set your number and CallMeBot key on your profile to send it.',
  'digest.sent': 'Sent.',
  'followUps.nothing': 'Nothing due.',
  'followUps.nothingMine': 'Nothing due for you.',
  'followUps.pressI': 'Press {key} to see everyone’s.',
  'card.stage': 'Stage',
  'card.status': 'Status',
  'card.action': 'Action',
  'card.notion': 'Notion',

  'np.title': 'New contact',
  'np.known': 'Known contact',
  'np.update': '— this will be saved as an update.',
  'np.nextContact': 'Next contact {date} ({n}d).',
  'np.notThem': 'Not them',
  'np.use': 'Use',
  'np.useTitle': 'Fill in their details',
  'np.defaults': 'Owner: you · Stage Lead · Status Not started · Next action First Contact.',
  'np.name': 'Name *',
  'np.namePlaceholder': 'Person',
  'np.org': 'Organization',
  'np.orgPlaceholder': 'Company (optional)',
  'np.notes': 'Notes *',
  'np.notesPlaceholder': 'What happened, what next?',
  'np.dictate': '🎤 Dictate',
  'np.stop': '■ Stop',
  'np.leadSource': 'Lead source',
  'np.nextFollowUp': 'Next follow-up',
  'np.add': 'Add contact',
  'np.addUpdate': 'Add update',
  'np.hint': 'saves · works offline, syncs when back',
  'np.unmapped': 'Your account is not mapped to a Notion person, so new contacts will have no owner until an admin maps you.',
  'np.added': 'Added {name}',
  'np.updateAdded': 'Update added to {name}',
  'np.clear': 'Clear',
  'np.extract': '✨ Extract with AI',
  'np.extracting': 'Extracting…',
  'np.proposals': 'Proposed by AI — click to accept or reject, nothing is saved until you add the contact.',
  'np.newOption': 'new option',
  'np.suggested': 'Suggested message',
  'np.suggestedAccept': 'Save as suggestion',
  'np.proposal.contactRole': 'Role',
  'np.proposal.industry': 'Industry',
  'np.proposal.nextAction': 'Next action',

  'log.allTypes': 'All types',
  'log.everyone': 'Everyone',
  'log.anyTime': 'Any time',
  'log.today': 'Today',
  'log.week7': 'Last 7 days',
  'log.month30': 'Last 30 days',
  'log.removedNotes': 'Removed notes',
  'log.entries': '{n} entries',
  'log.revertHint': 'revert selected',
  'log.empty': 'No log entries yet.',
  'log.noMatch': 'No log entries match.',
  'log.type': 'Type',
  'log.who': 'Who',
  'log.when': 'When',
  'log.contact': 'Contact',
  'log.what': 'What',
  'log.source': 'Source',
  'log.status': 'Status',
  'log.sourceNotion': 'Notion',

  'profile.email': 'Email',
  'profile.role': 'Role',
  'profile.notion': 'Notion person',
  'profile.unmapped': 'not mapped (ask an admin)',
  'profile.tz': 'Time zone',
  'profile.title': 'Profile',
  'profile.name': 'Display name',
  'profile.locale': 'Language',
  'profile.locale.en': 'English',
  'profile.locale.bg': 'Bulgarian (not translated yet)',
  'profile.tzPlaceholder': 'Europe/Sofia',
  'profile.save': 'Save',
  'profile.saved': 'Profile saved.',
  'profile.digest': 'Daily WhatsApp digest',
  'profile.digestLead': 'Every day at your hour you get the contacts you own that are due today or overdue. Nothing due, no message.',
  'profile.digestEnabled': 'Send me the digest',
  'profile.digestHour': 'At',
  'profile.phone': 'WhatsApp number',
  'profile.phonePlaceholder': '+359 888 123456',
  'profile.key': 'CallMeBot API key',
  'profile.keySet': 'key is set — type to replace',
  'profile.keyUnset': 'paste the key CallMeBot sent you',
  'profile.howTo': 'How to get a key: add +34 621 331 709 to your WhatsApp contacts, send it the message “I allow callmebot to send me messages”, and copy the API key from the reply.',
  'profile.sendTest': 'Send test',
  'profile.preview': 'Preview',
  'profile.lastSent': 'Last sent {date}',
  'profile.lastError': 'Last error: {error}',
  'profile.testSent': 'Sent to your WhatsApp ({n} characters).',
  'profile.testNoKey': 'Set your number and CallMeBot key first.',
  'profile.testFailed': 'Sending failed: {error}',
  'profile.channel': 'Send it via',
  'profile.channel.telegram': 'Telegram',
  'profile.channel.callmebot': 'WhatsApp (CallMeBot)',
  'profile.telegram': 'Telegram',
  'profile.telegramLead': 'Add contacts by text or voice from Telegram and get the digest there with buttons. Link once; the bot talks only to linked teammates.',
  'profile.telegramLinked': 'Linked as {name}',
  'profile.telegramLink': 'Link Telegram',
  'profile.telegramUnlink': 'Unlink',
  'profile.telegramCode': 'Open the bot and press Start, or send it /link {code}. The code works for 10 minutes.',
  'profile.telegramOpen': 'Open @{bot}',
  'profile.telegramRefresh': 'Linked? Refresh',
  'profile.telegramUnlinked': 'Telegram unlinked.',
  'profile.appearance': 'Appearance',
  'profile.appearanceHint': 'Remembered on this device.',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dim': 'Semi-dark',
  'theme.dark': 'Dark',

  'admin.title': 'Admin',
  'admin.syncHealth': 'Sync health',
  'admin.source': 'Source',
  'admin.notion': 'Notion',
  'admin.mock': 'Mock (no token)',
  'admin.lastPoll': 'Last poll',
  'admin.ok': 'ok',
  'admin.failed': 'failed',
  'admin.lastError': 'Last error',
  'admin.errors': 'Errors',
  'admin.lastWrite': 'Last write',
  'admin.crmLog': 'CRM Log',
  'admin.notCreated': 'not created',
  'admin.cursor': 'Cursor',
  'admin.rev': 'Rev',
  'admin.contacts': 'Contacts',
  'admin.liveTabs': 'Live tabs',
  'admin.loading': 'Loading…',
  'admin.syncNow': 'Sync now',
  'admin.resync': 'Force full resync',
  'admin.users': 'Users',
  'admin.invite': 'Invite',
  'admin.inviteEmail': 'Email',
  'admin.inviteRole': 'Role',
  'admin.invitePerson': 'Notion person',
  'admin.inviteButton': 'Invite',
  'admin.invited': 'Invited {email}.',
  'admin.role.member': 'member',
  'admin.role.admin': 'admin',
  'admin.noPerson': '— not mapped —',
  'admin.col.user': 'User',
  'admin.col.role': 'Role',
  'admin.col.person': 'Notion person',
  'admin.col.digest': 'Digest',
  'admin.viaTelegram': 'via Telegram',
  'admin.digestOn': 'on',
  'admin.digestOff': 'off',
  'admin.sendDigest': 'Send digest',
  'admin.copyInvite': 'Copy invite',
  'admin.inviteCopied': 'Invite text for {email} copied. Paste it into a message to them.',
  'admin.inviteText': 'Hi {name},\n\nYou have access to Mirror 360 CRM: {url}\n\nSign in with Google using {email} (that exact account). On a phone, open the link in Safari or Chrome and add it to your home screen; it works offline and opens on the New contact form.\n\nYou are {person} in Notion, so the contacts you own and your follow-ups are already there. Questions: ask {admin}.',
  'admin.inviteTextUnmapped': 'Hi {name},\n\nYou have access to Mirror 360 CRM: {url}\n\nSign in with Google using {email} (that exact account). On a phone, open the link in Safari or Chrome and add it to your home screen; it works offline and opens on the New contact form.\n\nQuestions: ask {admin}.',
  'admin.remove': 'Remove',
  'admin.removeConfirm': 'Remove {email}? They will no longer be able to sign in.',
  'admin.removed': 'Removed {email}.',
  'admin.digestBanner': 'Digest failing for {list}. Check their CallMeBot number and key.',
  'admin.digestSent': 'Digest sent to {email}.',
  'admin.digestNotSent': 'Not sent ({reason}).',

  'set.stage': 'Set stage',
  'set.nextAction': 'Set next action',
  'set.status': 'Set status',
  'set.owner': 'Set owner',
  'set.nextContactDate': 'Next contact date',
  'set.snoozeUntil': 'Snooze until',

  'toast.nothingToRevert': 'Nothing of yours to revert.',
  'toast.unmapped': 'Your account is not mapped to a Notion person yet; ask an admin.',
  'toast.conflict': 'Saved, but someone had changed this field meanwhile. The log shows what was overwritten.',
  'toast.couldNotSave': 'Could not save: {error}',
  'toast.couldNotRetry': 'Could not retry: {error}',
  'toast.retry': 'Retry',
  'toast.discard': 'Discard',
  'toast.dismiss': 'Dismiss',

  'common.empty': '—',
} as const;
